import React, { useState, useRef, useEffect } from 'react'

import Field from '../../Field/Field'
import TextInput from '../../TextInput/TextInput'
import Textarea from '../../Textarea/Textarea'
import Tooltip from '../../Tooltip/Tooltip'
import FieldLabel from '../../FieldLabel/FieldLabel'
import Checkbox from '../../Checkbox/Checkbox'
import Help from '../../Help/Help'
import ValidationMessage from '../../ValidationMessage/ValidationMessage'
import TagAsSelect from '../../TagAsSelect/TagAsSelect'
import { fieldUidRegex, getContentTypeFieldDataTestId, CONTENT_TYPE_FIELD_ACTION_IDS, NonLocalizableDom, handleToggleClick } from '../helper'
import ToggleSwitch from '../../ToggleSwitch/ToggleSwitch'
import { booleanTestIdFormatter } from './../../../utils/helpers'
import GroupFieldTitleToggle from "../HelperComponents/GroupFieldTitleToggle"
import CustomUserLimit from './CustomUserLimit'

export const onOptionsChangeMultipleHelper = (schema, checked) => {
  schema.field_metadata.ref_multiple = checked
  if (!checked) {
    if (schema.hasOwnProperty('min_instance')) {
      delete schema.min_instance
    }
    if (schema.hasOwnProperty('max_instance')) {
      delete schema.max_instance
    }
    if (schema.error && schema.error_details) {
      if (schema.error_details.hasOwnProperty('min_instance')) {
        delete schema.error_details.min_instance
      }
      if (schema.error_details.hasOwnProperty('max_instance')) {
        delete schema.error_details.max_instance
      }
    }
    if (schema.error_details && Object.keys(schema.error_details).length === 0) {
      schema.error = false
    }
  }
}

export const Reference = (props: any) => {
  const { onChange, parentInfo, uniqueKey, reference, schema, type, isNonLocalizable, hasNonLocalizablePlan, hasUserCustomFieldLimit, hasNonLocalizablePlanForBlocksAndGroupMultiple } = props
  const { loadMoreOptions, totalCounts, isMulti, value, updateTag, openModalCb, ...restReference } = reference

  const preSelectedVal = isMulti
    ? value.length
      ? value.map((val) => {
        return {
          id: val,
          label: val,
          title: val
        }
      })
      : []
    : value
  const [initialSchema, setSchema] = useState(schema)
  const [referenceValue, setReference] = useState(preSelectedVal)

  const { display_name, uid, field_metadata } = schema

  const displayNameRef: any = useRef(null)
  const [error, setError] = useState(fieldUidRegex(schema.uid, schema))

  useEffect(() => {
    displayNameRef?.current?.focus()
  }, [displayNameRef])

  const onchange = () => {
    onChange && onChange(schema)
    setSchema({ ...schema })
  }
  const onDisplayNameChange = (event: any) => {
    schema.display_name = event.target.value

    if (schema.hasOwnProperty('display_name') && schema.newfield) {
      schema.uid = schema.display_name
        .trim()
        .replace(/[^A-Z0-9]+/gi, '_')
        .toLowerCase()
      if (schema.display_name) {
        schema.error_details = {
          ...schema.error_details,
          uid: ''
        }
      }
    }

    if (!schema.error_details) {
      schema.error_details = {}
    }

    if (!schema.display_name.length) {
      schema.error_details = {
        ...schema.error_details,
        display_name: 'Display Name cannot be empty'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        display_name: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }
    onchange()
  }
  const onUidChange = (event: any) => {
    setError(fieldUidRegex(event.target?.value?.trim(), schema))

    if (!schema.error_details) {
      schema.error_details = {}
    }

    if (!schema.uid.length) {
      schema.error_details = {
        ...schema.error_details,
        uid: 'uid cannot be empty'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        uid: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }
    onchange()
  }
  const onInstructionChange = (event: any) => {
    field_metadata.instruction = event.target.value
    onchange()
  }
  const onHelpTextChange = (event: any) => {
    field_metadata.description = event.target.value
    onchange()
  }

  const onOptionsChange = (event: any) => {
    if (event.target.name === `${uniqueKey}.mandatory`) {
      schema.mandatory = event.target.checked
    }
    if (event.target.name === `${uniqueKey}.multiple`) {
      onOptionsChangeMultipleHelper(schema, event.target.checked)
    }
    if (event.target.name === `${uniqueKey}.nonLocalization`) {
      if (event.target.checked && parentInfo.hasParent && hasNonLocalizablePlanForBlocksAndGroupMultiple) {
        handleToggleClick(event, parentInfo.data_type, schema, onchange, 'nonLocalizable', props.t , parentInfo.isInsideMultiple ,parentInfo.ancestorWithMultipleType)
      }
      else {
        schema.non_localizable = event.target.checked
      }
    }
    onchange()
  }

  const handleRefUpdate = (selectedRefs: any) => {
    let newSelectedRef = isMulti
      ? selectedRefs.length
        ? selectedRefs.map((val) => {
          return {
            id: val,
            label: val,
            title: val
          }
        })
        : []
      : selectedRefs

    setReference(newSelectedRef)
    schema.reference_to = selectedRefs

    if (schema.reference_to && !schema.reference_to.length) {
      schema.error_details = {
        ...schema.error_details,
        reference_to: 'Please select a content type'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        reference_to: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }

    onchange()
  }

  if (type === 'basic') {
    return (
      <div className="FormFields">
        <Field>
          <FieldLabel version='v2' className='FieldLabel__CT' required htmlFor="displayName">
            Display Name
          </FieldLabel>
          <TextInput
            version='v2'
            required
            name={`${uniqueKey}.displayName`}
            value={display_name}
            onChange={onDisplayNameChange}
            inputRef={displayNameRef}
            error={schema.error && schema.error_details.display_name ? true : false}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.DISPLAY_NAME_INPUT)}
          />
        </Field>
        <Field>
          <FieldLabel version='v2' className='FieldLabel__CT' required htmlFor="uid">
            Unique ID
          </FieldLabel>
          <Help type="basic" text="A unique ID for this field. This should not be same as the ID of any other field in this content type." />
          <TextInput
            version='v2'
            required
            name={`${uniqueKey}.uid`}
            value={uid}
            onChange={onUidChange}
            error={(schema.error && schema.error_details.uid) || error ? true : false}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.UID_INPUT)}
          />
        </Field>
        <Field>
          <FieldLabel version='v2' className='FieldLabel__CT' htmlFor="referencedCT">Referenced Content Type</FieldLabel>
          <TagAsSelect
            version='v2'
            placeholder={'Select'}
            showEmptyState={true}
            isMulti={isMulti}
            showCount={true}
            tags={
              isMulti
                ? referenceValue
                : referenceValue && referenceValue.length > 0
                  ? [{ label: referenceValue, value: referenceValue }]
                  : []
            }
            updateTag={handleRefUpdate}
            openModalCb={(callback) => openModalCb(callback)}
            {...restReference}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.REFERENCE_TAG_AS_SELECT)}
          />

          {schema.error && schema.error_details.reference_to ? (
            <ValidationMessage version='v2' className={'error'}>{schema.error_details.reference_to}</ValidationMessage>
          ) : null}
        </Field>
        <Field>
          <FieldLabel version='v2' className='FieldLabel__CT' htmlFor="instruction">Instruction Value</FieldLabel>
          <Textarea
            version='v2'
            name={`${uniqueKey}.instruction`}
            value={field_metadata.instruction || ''}
            onChange={onInstructionChange}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            // multiline={true}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.INSTRUCTION_INPUT)}
            id={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.INSTRUCTION_INPUT)}
          />
        </Field>

        <Field>
          <FieldLabel version='v2' className='FieldLabel__CT' htmlFor="helpText">Help Text</FieldLabel>
          <Textarea
            version='v2'
            name={`${uniqueKey}.helpText`}
            value={field_metadata.description || ''}
            onChange={onHelpTextChange}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            // multiline={true}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.HELP_TEXT_INPUT)}
            id={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.HELP_TEXT_INPUT)}
          />
        </Field>
      </div>
    )
  } else if (type === 'advanced') {
    return (
      <div className="FormFields">
        <Field className='LastField'>
          <FieldLabel version='v2' className='FieldLabel__CT' htmlFor="options">Other Options</FieldLabel>
          <div className='ToggleWrap' >
            <ToggleSwitch
              testId={booleanTestIdFormatter("cs-ct-field-reference-mandatory", schema.mandatory)}
              name={`${uniqueKey}.mandatory`}
              label="Mandatory"
              checked={schema.mandatory}
              onChange={onOptionsChange}
            />
          </div>
          <div className={`ToggleWrap ${hasUserCustomFieldLimit ? 'flex-v-center mb-0' : ''}`}>
            <ToggleSwitch
              testId={booleanTestIdFormatter("cs-ct-field-reference-multiple", schema.field_metadata.ref_multiple)}
              name={`${uniqueKey}.multiple`}
              label="Multiple"
              checked={schema.field_metadata.ref_multiple}
              onChange={onOptionsChange}
            />
            {hasUserCustomFieldLimit &&
              <div>
                <Help type="basic" className='mb-0 ml-8' iconName='Information' text={props.t('content_type.save_content_type.body.fields.tooltip_text.multiple') || "If enabled, you can define the minimum and maximum instances allowed for the field"} />
              </div>
            }
          </div>
          {hasUserCustomFieldLimit && schema?.field_metadata?.ref_multiple ? (
            <CustomUserLimit
              uniqueKey={uniqueKey}
              schema={schema}
              onchange={onchange}
              t={props.t}
              type={type}
            />
          ) : null}
          {
            hasNonLocalizablePlan ? (
              hasNonLocalizablePlanForBlocksAndGroupMultiple ? (
                <NonLocalizableDom
                  isFieldPresent={true}
                  uniqueKey={uniqueKey}
                  schema={schema}
                  onOptionsChange={onOptionsChange}
                  dataTestId={booleanTestIdFormatter(schema.nonLocParams?.insideMultiple ? `cs-ct-field-reference-non-localizable-parent-multiple` : `cs-ct-field-reference-non-localizable`, schema.non_localizable)}
                  dataTestIdForDescription={"cs-ct-field-reference-non-localizable-description"}
                  isNonLocalizable={isNonLocalizable}
                  isAncestorNonLoc ={parentInfo.isAncestorNonLoc}
                  hasUserCustomFieldLimit={hasUserCustomFieldLimit}
                />
              ) : (parentInfo.hasParent ? !parentInfo.isInsideMultiple : true) ?
                <NonLocalizableDom
                  isFieldPresent={true}
                  uniqueKey={uniqueKey}
                  schema={schema}
                  onOptionsChange={onOptionsChange}
                  dataTestId={booleanTestIdFormatter("cs-ct-field-reference-non-localizable", schema.non_localizable)}
                  dataTestIdForDescription={"cs-ct-field-reference-non-localizable-description"}
                  isNonLocalizable={isNonLocalizable}
                  isAncestorNonLoc ={parentInfo.isAncestorNonLoc}
                  hasUserCustomFieldLimit={hasUserCustomFieldLimit}
                />
                : null
            ) : null
          }
        </Field>
        <GroupFieldTitleToggle
          parentInfo={parentInfo}
          uniqueKey={uniqueKey}
          field_metadata={field_metadata}
          onchange={onchange}
          multiple={false} // set as false only in case of reference field as we allow reference field to be set as multiple and group title at once
          testId="cs-ct-field-reference-group-title"
        />
      </div>
    )
  }
}

export default React.memo(Reference)
